/**
 * Route-shape predicates for the storefront tree. Pure, no DOM, no router — so
 * they unit-test without dragging `src/` (and therefore `src/lib/supabase.ts`)
 * into the test run.
 *
 * These answer "what is this path, structurally?" for code that has to decide
 * something BEFORE React Router has matched anything — chunk prefetching being
 * the case that motivated them.
 */

/**
 * Every checkout surface in App.tsx ends in a literal `/checkout` segment: the
 * site-root form (`/checkout`), the per-page routes (`/p/<slug>/checkout` and
 * `/<slug>/checkout`) and the team-prefixed one (`/<handle>/<slug>/checkout`).
 * Matching on the last segment therefore covers all four without a router.
 *
 * The leading slash in the needle is what keeps a page slugged `pre-checkout`
 * out: `/pre-checkout` does not end in `/checkout`. A page slugged exactly
 * `checkout` at `/p/checkout` DOES match — but so does React Router, which
 * ranks the static `/:slug/checkout` above `/p/:slug` and renders the checkout
 * there too, so the two agree.
 */
export function isCheckoutPath(pathname: string): boolean {
  const path = stripTrailingSlash((pathname || "").split("#")[0]?.split("?")[0] ?? "");
  return path === "/checkout" || path.endsWith("/checkout");
}

/** The storefront root — the only path that renders StoreHome. */
export function isStorefrontRootPath(pathname: string): boolean {
  return stripTrailingSlash((pathname || "").split("#")[0]?.split("?")[0] ?? "") === "";
}

/** `/a/b/` -> `/a/b`; `/` -> `""`. Keeps the two predicates agreeing on slashes. */
function stripTrailingSlash(path: string): string {
  return path.replace(/\/+$/, "");
}
